import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaEnvelope, FaKey, FaPhone, FaSearch, FaUsers, FaUserTie } from 'react-icons/fa';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import './DepartamentosDetalle.css';

const Arrendados = () => {
    const { fraccionamientoId } = useAuth();
    const [arrendados, setArrendados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (!fraccionamientoId) return;

        const fetchArrendados = async () => {
            try {
                setLoading(true);

                // Casas
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const casasSnap = await getDocs(query(casasRef, orderBy('numero')));
                const normalize = (s) => String(s || '').replace(/\s+/g, '').toUpperCase();
                const casasData = casasSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                // Contacts (propietarios/arrendatarios) en colección separada
                let contactsMap = new Map();
                try {
                    const contactsRef = collection(db, 'fraccionamientos', fraccionamientoId, 'contacts');
                    const contactsSnap = await getDocs(contactsRef);
                    contactsMap = new Map();
                    contactsSnap.forEach(d => {
                        const data = d.data();
                        const numero = normalize(data.numero_vivienda || d.id || '');
                        if (numero) contactsMap.set(numero, data);
                    });
                } catch (contactErr) {
                    console.warn('Arrendados: sin permisos para contactos, se omiten datos de propietario', contactErr);
                    setErrorMsg('No hay permisos para ver datos de propietarios; se muestran solo los datos de las casas.');
                }

                // Identificar arrendadas SOLO por perfil en casas; contactos solo complementan datos
                const rentalProfiles = new Set(['arrendador', 'arrendatario', 'arrendataria', 'arrendada', 'arrendado', 'inquilino', 'renter', 'rentado']);
                const rented = casasData
                    .filter(c => rentalProfiles.has((c.perfil || '').toLowerCase()))
                    .map(c => {
                        const numeroRaw = String(c.numero || c.numeroCasa || c.lote || '').trim();
                        const numeroKey = normalize(numeroRaw);
                        const contact = contactsMap.get(numeroKey) || {};
                        const hasContact = Object.keys(contact).length > 0;

                        return {
                            id: c.id,
                            numero: numeroRaw || '—',
                            nombre: c.nombre_residente || c.nombreCompleto || c.nombre || 'Sin registro',
                            telefono: c.telefono || c.telefonoPrincipal || '',
                            telefono2: c.telefono2 || c.telefonoAlternativo || '',
                            email: c.correo || c.email || '',
                            num_residentes: c.num_residentes || c.numResidentes || c.ocupantes || 0,
                            emergencia: c.emergencia || {},
                            perfil: c.perfil || 'No especificado',
                            ownerContact: hasContact ? {
                                nombre_real_propietario: contact.nombre_propietario || contact.propietario || contact.nombre_real_propietario || '',
                                telefono: contact.telefono_arrendador || contact.telefono || '',
                                telefono2: contact.telefono2 || '',
                                email: contact.email_arrendador || contact.email || '',
                                arrendador: contact.arrendador || '',
                            } : null,
                        };
                    });

                setArrendados(rented);
            } catch (error) {
                console.error('Error cargando arrendados:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchArrendados();
    }, [fraccionamientoId]);

    const filteredArrendados = arrendados.filter((arr) => {
        const term = searchTerm.toLowerCase();
        return (
            String(arr.numero).toLowerCase().includes(term) ||
            arr.nombre.toLowerCase().includes(term) ||
            arr.email.toLowerCase().includes(term) ||
            arr.telefono.toLowerCase().includes(term)
        );
    });

    return (
        <div className="departamentos-page">
            <div className="departamentos-container">
                <header className="departamentos-header">
                    <div className="departamentos-header-icon">
                        <FaKey />
                    </div>
                    <div>
                        <h1>Viviendas arrendadas</h1>
                        <p>Consulta el listado de viviendas en arrendamiento activo.</p>
                    </div>
                    <Link to="/administracion/departamentos/identificacion" className="departamentos-back-link">
                        <FaChevronLeft /> Regresar
                    </Link>
                </header>

                <section className="departamentos-content">
                    <div className="search-bar-container">
                        <div className="search-bar">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Buscar por casa, nombre, email o teléfono..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="search-results-count">
                            {filteredArrendados.length} {filteredArrendados.length === 1 ? 'vivienda encontrada' : 'viviendas encontradas'}
                        </div>
                    </div>

                    {errorMsg && (
                        <div className="warning-banner">
                            {errorMsg}
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-state">Cargando viviendas arrendadas...</div>
                    ) : (
                        <div className="departamentos-grid">
                            {filteredArrendados.length === 0 ? (
                                <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                                    <FaKey style={{ fontSize: '48px', color: 'rgba(255,255,255,0.5)' }} />
                                    <p>No hay viviendas arrendadas que coincidan con tu búsqueda.</p>
                                </div>
                            ) : (
                                filteredArrendados.map((arr) => (
                                    <article key={arr.id} className="departamento-card">
                                        <div className="card-header">
                                            <div className="card-icon">
                                                {arr.numero}
                                            </div>
                                            <div className="card-title-text">
                                                <h3>{arr.nombre}</h3>
                                                <p>Casa {arr.numero}</p>
                                            </div>
                                        </div>

                                        <div className="card-body">
                                            {arr.telefono && (
                                                <div className="resident-info-row">
                                                    <FaPhone color="#f59e0b" />
                                                    <div>
                                                        <span className="info-label">Teléfono</span>
                                                        <span className="info-value">{arr.telefono}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {arr.telefono2 && (
                                                <div className="resident-info-row">
                                                    <FaPhone color="#f59e0b" />
                                                    <div>
                                                        <span className="info-label">Teléfono 2</span>
                                                        <span className="info-value">{arr.telefono2}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {arr.email && (
                                                <div className="resident-info-row">
                                                    <FaEnvelope color="#f59e0b" />
                                                    <div>
                                                        <span className="info-label">Email</span>
                                                        <span className="info-value">{arr.email}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {arr.num_residentes > 0 && (
                                                <div className="resident-info-row">
                                                    <FaUsers color="#f59e0b" />
                                                    <div>
                                                        <span className="info-label">Residentes</span>
                                                        <span className="info-value">{arr.num_residentes}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {arr.ownerContact?.nombre_real_propietario && (
                                                <div className="resident-info-row">
                                                    <FaUserTie color="#10b981" />
                                                    <div>
                                                        <span className="info-label">Propietario</span>
                                                        <span className="info-value">{arr.ownerContact.nombre_real_propietario}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {arr.ownerContact?.telefono && (
                                                <div className="resident-info-row">
                                                    <FaPhone color="#10b981" />
                                                    <div>
                                                        <span className="info-label">Teléfono Propietario</span>
                                                        <span className="info-value">{arr.ownerContact.telefono}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {arr.ownerContact?.email && (
                                                <div className="resident-info-row">
                                                    <FaEnvelope color="#10b981" />
                                                    <div>
                                                        <span className="info-label">Email Propietario</span>
                                                        <span className="info-value">{arr.ownerContact.email}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                ))
                            )}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Arrendados;
